import { categories } from './types.ts';
import type { Category, Exercise, Progress, Question } from './types.ts';

export function localDay(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
export function dayNumber(day: string): number {
  return Date.parse(`${day}T00:00:00Z`) / 86_400_000;
}
export function shiftDay(day: string, offset: number): string {
  return new Date((dayNumber(day) + offset) * 86_400_000).toISOString().slice(0, 10);
}
export function freshProgress(): Progress {
  return { version: 1, attempts: {}, drafts: {}, history: [], completions: {}, readingStarted: {} };
}
/** Strict numeric grammar: never evaluate user input as JavaScript. */
export function parseNumber(input: string): number | null {
  const text = input.trim();
  const decimal = '[+-]?(?:\\d+(?:\\.\\d*)?|\\.\\d+)';
  const match = text.match(new RegExp(`^(${decimal})(?:\\s*\\/\\s*(${decimal}))?\\s*(%)?$`));
  if (!match) return null;
  const denominator = match[2] ? Number(match[2]) : 1;
  if (denominator === 0) return null;
  const result = Number(match[1]) / denominator / (match[3] ? 100 : 1);
  return Number.isFinite(result) ? result : null;
}
export function grade(question: Question, input: string): boolean | null {
  if (question.answer.kind === 'choice') {
    if (!question.options?.some((option) => option.id === input)) return null;
    return input === question.answer.value;
  }
  const value = parseNumber(input);
  return value === null
    ? null
    : Math.abs(value - question.answer.value) <= question.answer.tolerance;
}
export const attemptKey = (context: string, questionId: string) => `${context}/${questionId}`;

export function submit(
  progress: Progress,
  exercise: Exercise,
  question: Question,
  input: string,
  context: string,
  selectedDay: string,
  today: string,
): Progress {
  const correct = grade(question, input);
  if (correct === null) return progress;
  const key = attemptKey(context, question.id);
  const previous = progress.attempts[key];
  if (previous?.correct) return progress;
  const next: Progress = {
    ...progress,
    attempts: {
      ...progress.attempts,
      [key]: { tries: (previous?.tries ?? 0) + 1, correct, lastAnswer: input },
    },
    history: [...progress.history],
  };
  if (!correct && !next.history.some((row) => row.questionId === question.id)) {
    next.history.push({ questionId: question.id, exerciseId: exercise.id, addedAt: today });
  }
  if (
    selectedDay === today &&
    exercise.questions.every((item) => next.attempts[attemptKey(context, item.id)])
  ) {
    next.completions = {
      ...progress.completions,
      [today]: [...new Set([...(progress.completions[today] ?? []), exercise.category])],
    };
  }
  return next;
}
export function dismiss(progress: Progress, questionId: string): Progress {
  return { ...progress, history: progress.history.filter((row) => row.questionId !== questionId) };
}
export function streak(progress: Progress, today: string, category?: Category): number {
  const done = (day: string) =>
    category
      ? progress.completions[day]?.includes(category)
      : categories.every((item) => progress.completions[day]?.includes(item));
  let cursor = done(today) ? today : shiftDay(today, -1);
  let count = 0;
  while (done(cursor)) {
    count++;
    cursor = shiftDay(cursor, -1);
  }
  return count;
}
